import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material'

import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import GoogleIcon from '../../../../components/icon/GoogleIcon'
import {
  UI_COLOR_BORDER_CANCEL,
  UI_COLOR_BORDER_NEUTRAL,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_PRIMARY_PRESS_ALT,
  UI_COLOR_PRIMARY_SOFT_ALT,
  UI_COLOR_SURFACE_NEUTRAL,
  UI_COLOR_TEXT_CANCEL,
  UI_COLOR_TEXT_MUTED_ALT,
  UI_COLOR_TEXT_TERTIARY
} from '../../../../constants/uiColors'
import RefundApprovalConfirmButtonLabel from './RefundApprovalConfirmButtonLabel'
import RefundApprovalSummaryRows from './RefundApprovalSummaryRows'

const REJECT_REASON_MAX_LENGTH = 24

const RefundApprovalConfirmDialogs = ({
  lineToReject,
  confirmAction,
  loading,
  rejectReason,
  summaryItems,
  onCloseLineReject,
  onConfirmLineReject,
  onCloseApprove,
  onConfirmApprove,
  onCloseReject,
  onConfirmReject,
  onRejectReasonChange
}) => (
  <>
    <Dialog open={Boolean(lineToReject)} onClose={() => !loading && onCloseLineReject()} fullWidth>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>
        {REFUND_APPROVAL_TEXT.rejectLineTitle}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14 }}>
          {REFUND_APPROVAL_TEXT.rejectLineBodyPrefix}{' '}
          <strong>{lineToReject?.product_name || lineToReject?.product_db_ref}</strong>{' '}
          {REFUND_APPROVAL_TEXT.rejectLineBodySuffix}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCloseLineReject}
          disabled={loading}
          sx={{ minWidth: 112, borderColor: UI_COLOR_BORDER_CANCEL, color: UI_COLOR_TEXT_CANCEL }}
        >
          {REFUND_APPROVAL_TEXT.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirmLineReject}
          disabled={loading}
          disableElevation
          sx={{
            minWidth: 156,
            backgroundColor: UI_COLOR_PRIMARY_ALT,
            '&:hover': { backgroundColor: UI_COLOR_PRIMARY_PRESS_ALT }
          }}
        >
          {REFUND_APPROVAL_TEXT.rejectLine}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={confirmAction === 'approve'} onClose={onCloseApprove} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 500 }}>
          {REFUND_APPROVAL_TEXT.approveRefundTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            border: `1.5px solid ${UI_COLOR_BORDER_NEUTRAL}`,
            borderRadius: 4,
            backgroundColor: UI_COLOR_SURFACE_NEUTRAL,
            px: 2,
            py: 1.25
          }}
        >
          <RefundApprovalSummaryRows items={summaryItems} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCloseApprove}
          disabled={loading}
          sx={{
            minWidth: { xs: 0, sm: 112 },
            flex: { xs: '0 1 112px', sm: '0 0 auto' },
            borderColor: UI_COLOR_BORDER_CANCEL,
            color: UI_COLOR_TEXT_CANCEL
          }}
        >
          {REFUND_APPROVAL_TEXT.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirmApprove}
          disabled={loading}
          disableElevation
          sx={{
            minWidth: { xs: 0, sm: 220 },
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            backgroundColor: UI_COLOR_PRIMARY_SOFT_ALT,
            '&:hover': { backgroundColor: UI_COLOR_PRIMARY_ALT }
          }}
        >
          <RefundApprovalConfirmButtonLabel />
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={confirmAction === 'reject'}
      onClose={() => {
        if (loading) return
        onCloseReject()
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 500 }}>
          {REFUND_APPROVAL_TEXT.rejectRefundTitle}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 11,
              fontWeight: 500,
              color: UI_COLOR_TEXT_MUTED_ALT,
            }}
          >
            <GoogleIcon name="info" size={14} color={UI_COLOR_TEXT_MUTED_ALT} />
            {REFUND_APPROVAL_TEXT.rejectRefundNotice}
          </Typography>
        </Box>

        <Box
          sx={{
            border: `1.5px solid ${UI_COLOR_BORDER_NEUTRAL}`,
            borderRadius: 4,
            backgroundColor: UI_COLOR_SURFACE_NEUTRAL,
            px: 2,
            py: 1.25,
            mb: 3
          }}
        >
          <RefundApprovalSummaryRows items={summaryItems} />
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={REFUND_APPROVAL_TEXT.rejectReasonLabel}
          placeholder={REFUND_APPROVAL_TEXT.rejectReasonPlaceholder}
          value={rejectReason}
          onChange={(event) =>
            onRejectReasonChange(event.target.value.slice(0, REJECT_REASON_MAX_LENGTH))
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: REJECT_REASON_MAX_LENGTH }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCloseReject}
          disabled={loading}
          sx={{ minWidth: 112, borderColor: UI_COLOR_BORDER_CANCEL, color: UI_COLOR_TEXT_CANCEL }}
        >
          {REFUND_APPROVAL_TEXT.cancel}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirmReject}
          disabled={loading}
          disableElevation
          sx={{ minWidth: 176 }}
        >
          {REFUND_APPROVAL_TEXT.confirmReject}
        </Button>
      </DialogActions>
    </Dialog>
  </>
)

export default RefundApprovalConfirmDialogs
